import { CurrencyPipe, DatePipe, TitleCasePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { IconAndButtonCardComponent } from '@components/icon-and-button-card/icon-and-button-card.component';
import { QuestionnaireAnswers } from '@enums/questionnaire-answers';
import { StorageKeys } from '@enums/storage-keys';
import { environment } from '@environments/environment';
import { Payment } from '@models/payment';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { ErrorLoggingService } from '@services/error-logging.service';
import { OrderService } from '@services/order.service';
import { StorageService } from '@services/storage.service';

@Component({
  selector: 'app-trt-consultation-request-completed',
  templateUrl: './trt-consultation-request-completed.component.html',
  standalone: true,
  styleUrls: ['./trt-consultation-request-completed.component.scss'],
  imports: [IconAndButtonCardComponent, TitleCasePipe, CurrencyPipe, DatePipe],
})
export class TrtConsultationRequestCompletedComponent implements OnInit {
  public loadingButton = false;
  public patientName: string;
  public transactionId: string;
  public payments: Payment[] = null;
  public hasMandatoryLabResult: string;
  public contactEmail: string;
  public isInHomeCollection: boolean = false;

  protected readonly QuestionnaireAnswers = QuestionnaireAnswers;

  private consultationStorageService = inject(ConsultationStorageService);
  private orderService = inject(OrderService);
  private errorLoggingService = inject(ErrorLoggingService);
  private consultationRequestService = inject(ConsultationRequestService);
  private hash: string;
  private storage = inject(StorageService);

  /**
   * Initializes the component.
   */
  public ngOnInit(): void {
    this.patientName = this.consultationStorageService.patient?.name ?? '';
    this.transactionId = this.consultationStorageService.transactionId;
    this.hash = this.consultationStorageService.hash;
    this.hasMandatoryLabResult = this.consultationStorageService.hasMandatoryLabResult;
    this.contactEmail = environment.email;
    this.isInHomeCollection = !!this.storage.inHomeBookingResult;
    this.getPayments();
    this.consultationRequestService.clearLastConsultationRequestData();
    this.storage.remove(StorageKeys.InHomeBookingResult);
    this.storage.remove(StorageKeys.IsIhcPaid);
  }

  /**
   * Download order invoice.
   */
  public downloadInvoice(): void {
    this.loadingButton = true;
    this.orderService.downloadInvoice(this.transactionId, this.hash).subscribe({
      next: (response) => {
        this.createAndDownloadInvoice(response);
        this.loadingButton = false;
      },
      error: (error) => {
        this.loadingButton = false;
        this.errorLoggingService.logError(`Invoice download failed: ${error.message}`);
      },
    });
  }

  /**
   * Redirect to the given link.
   */
  public redirect(): void {
    window.location.href = environment.siteUrls.home;
  }

  /**
   * Get the payments history for the order.
   */
  private getPayments(): void {
    if (!this.transactionId || !this.hash) {
      this.payments = [];

      return;
    }

    this.orderService.orderPayments(this.transactionId, this.hash).subscribe({
      next: (response) => (this.payments = response),
      error: (error) => {
        this.payments = [];
        this.errorLoggingService.logError(`Loading payments failed: ${error.message}`);
      },
    });
  }

  /**
   * Create and download invoice.
   *
   * @param {Blob} invoice - The file data.
   */
  private createAndDownloadInvoice(invoice: Blob): void {
    const url = window.URL.createObjectURL(invoice);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'invoice.pdf';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}
